import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, Sun, Moon, Compass, Heart, Waves,
  ChevronDown, BookOpen, Feather, Mail, Users, Sparkle,
} from "lucide-react";
import PersonaliseCTA from "@/components/earthpulse/PersonaliseCTA";
import SocialProof from "@/components/earthpulse/SocialProof";
import MeditationRecommender from "@/components/earthpulse/MeditationRecommender";
import InstallHomescreen from "@/components/earthpulse/InstallHomescreen";
import LandingActionBar from "@/components/earthpulse/LandingActionBar";
import PosterGallery from "@/components/earthpulse/PosterGallery";
import SupportCreator from "@/components/earthpulse/SupportCreator";
import QuoteMarquee from "@/components/earthpulse/QuoteMarquee";
import SharePagePreview from "@/components/earthpulse/SharePagePreview";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { getSchumannCurrent, getCosmicCurrent, subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

/**
 * Landing page for earthpulse.store — designed to help visitors feel SEEN,
 * calm down, and understand why they might buy the birth chart or open the
 * daily guidance. Long, slow, spacious. Sections separated by generous vertical
 * air. Prose leans toward highly sensitive people (HSP) and those who feel
 * space-weather shifts in their bodies.
 */
export default function Landing() {
  return (
    <div className="min-h-screen relative z-10 overflow-x-hidden">
      <TopNav />
      <Hero />
      <QuoteMarquee />
      <Empathy />
      <Science />
      <MeetTheAlmanac />
      <InstallHomescreen />
      <SupportCreator />
      <BirthChartTeaser />
      <HowItWorks />
      <Testimonials />
      <SharePagePreview />
      <FAQ />
      <Newsletter />
      <Footer />
      <LandingActionBar />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NAV
// ─────────────────────────────────────────────────────────────
function IntroReelReplacement() {
  return <PosterGallery />;
}

function TopNav() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[color:var(--gold)]/12" style={{ background: "rgba(5,9,23,0.72)" }}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home">
          <img src="/logo.svg" alt="Earth Pulse" className="w-9 h-9 rounded-full" />
          <div className="leading-tight">
            <div className="font-serif-display text-[color:var(--parchment)] text-lg tracking-tight">Earth · Pulse</div>
            <div className="label-overline text-[10px] hidden md:block">for sensitive souls</div>
          </div>
        </Link>
        <nav className="flex items-center gap-3 md:gap-6">
          <Link to="/almanac" className="hidden md:inline label-overline hover:text-[color:var(--parchment)] transition" data-testid="nav-almanac">
            Today's guidance
          </Link>
          <a href="#install-app" className="hidden lg:inline label-overline hover:text-[color:var(--parchment)] transition" data-testid="nav-install">
            Install app
          </a>
          <ReadingsMenu />
        </nav>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Cosmic Readings dropdown — advertises the £8.88/mo membership + shortcuts
// (Birth Chart, Compatibility, Monthly Forecast) from a single gold pill.
// ─────────────────────────────────────────────────────────────
function ReadingsMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--gold-soft)]"
          data-testid="nav-readings-trigger"
          aria-label="Open Premium Services menu"
        >
          <Sparkles size={13} />
          <span>Premium Services</span>
          <ChevronDown size={13} className="opacity-80" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="p-0 w-[340px] rounded-2xl border border-[color:var(--gold)]/25 bg-[rgba(5,9,23,0.96)] backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
        data-testid="nav-readings-menu"
      >
        <div className="px-5 pt-4 pb-3 border-b border-[color:var(--gold)]/12">
          <div className="label-overline text-[10px] text-[color:var(--gold)]">£8.88 / month · unlimited</div>
          <div className="mt-1 font-serif-italic text-[color:var(--parchment-soft)] text-sm">
            Every chart, forever · screenshot &amp; share · cancel anytime
          </div>
        </div>

        <ReadingsMenuItem
          to="/birth-chart"
          icon={Sparkles}
          title="Cosmic Birth Chart"
          blurb="Sun, Moon, Rising and the sky the moment you arrived — screenshot-ready natal wheel."
          testid="nav-readings-birth-chart"
        />
        <div className="mx-5 h-px bg-[color:var(--gold)]/10" />
        <ReadingsMenuItem
          to="/compatibility"
          icon={Users}
          title="Synastry · you & them"
          blurb="Two charts, one honest map — where you spark, where you'll need patience."
          testid="nav-readings-compatibility"
          accent="rose"
        />
        <div className="mx-5 h-px bg-[color:var(--gold)]/10" />
        <ReadingsMenuItem
          to="/subscribe"
          icon={Sparkle}
          title="Start membership · £8.88/mo"
          blurb="Unlock unlimited readings — Birth Chart & Compatibility. Cancel anytime."
          testid="nav-readings-subscribe"
        />

        <div className="px-5 py-3 border-t border-[color:var(--gold)]/12 flex items-center justify-between gap-3">
          <Link
            to="/almanac"
            className="inline-flex items-center gap-2 label-overline text-[10px] text-[color:var(--gold)] hover:text-[color:var(--parchment)] transition"
            data-testid="nav-readings-almanac"
          >
            free daily guidance <ArrowRight size={11} />
          </Link>
          <Link
            to="/subscription"
            className="label-overline text-[10px] text-[color:var(--text-secondary)] hover:text-[color:var(--parchment)] transition"
            data-testid="nav-readings-manage"
          >
            manage
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ReadingsMenuItem({ to, icon: Icon, title, blurb, testid, accent }) {
  const color = accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  return (
    <Link
      to={to}
      className="group flex items-start gap-3 px-5 py-4 hover:bg-[color:var(--gold)]/8 transition"
      data-testid={testid}
    >
      <div
        className="mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition"
        style={{ borderColor: `${color === "var(--rose-gold)" ? "rgba(229,168,124,0.35)" : "rgba(212,162,76,0.35)"}` }}
      >
        <Icon size={14} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="font-serif-display text-[color:var(--parchment)] text-base leading-tight group-hover:text-white transition">
          {title}
        </div>
        <div className="mt-1 font-sans-body text-[color:var(--text-secondary)] text-xs leading-snug">
          {blurb}
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// HERO — centred, sells the unique experience
// ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 pt-14 md:pt-24 pb-24 md:pb-32">
        <div className="text-center stagger-in max-w-4xl mx-auto">
          <div className="label-overline">Daily cosmic guidance</div>
          <h1 className="mt-6 font-serif-display text-[color:var(--parchment)] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight">
            For the ones who <span className="font-serif-italic text-[color:var(--gold)]">feel it</span> first.
          </h1>
          <p className="mt-8 md:mt-10 text-[color:var(--parchment)] text-xl md:text-2xl leading-[1.35] font-serif-italic max-w-3xl mx-auto">
            Earth Pulse tells you the energetic changes moving through today —
            <span className="text-[color:var(--gold)]"> space weather</span>,
            <span className="text-[color:var(--gold)]"> Earth&apos;s heartbeat</span>, and
            <span className="text-[color:var(--gold)]"> your star alignment at birth</span>{" "}—
            all in one warm, honest place.
          </p>
          <p className="mt-6 md:mt-8 text-[color:var(--parchment-soft)] text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-sans-body">
            Some days your body knows before the news does — headaches without cause, restless sleep, a quiet ache in the chest.
            We measure today&apos;s <span className="text-[color:var(--gold)]">space weather</span> — solar wind, geomagnetic storms and X-ray flares — read Earth&apos;s Schumann resonance humming beneath it, and weave both with your personal chart so every visit is genuinely
            <em className="font-serif-italic text-[color:var(--gold)] not-italic"> yours</em>. Not a horoscope. Not a subscription. Not a data harvest.
          </p>

          {/* Three-pillar promise */}
          <div className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto text-left">
            {[
              { title: "Space weather",     note: "Live Kp, solar wind, and X-ray flux from NOAA — the Sun's mood, told plainly." },
              { title: "Earth's heartbeat", note: "The 7.83 Hz Schumann resonance — what the planet's field is doing right now." },
              { title: "Your star alignment", note: "Sun · Moon · Rising from your birth chart — the sky at the moment you arrived." },
            ].map((p) => (
              <div key={p.title}
                   className="rounded-2xl border border-[color:var(--gold)]/25 p-5 md:p-6 h-full"
                   style={{ background: "rgba(10, 17, 40, 0.5)" }}>
                <div className="label-overline text-[color:var(--gold)]/90">{p.title}</div>
                <div className="mt-2 text-[color:var(--parchment-soft)] font-serif-italic text-sm md:text-base leading-relaxed">
                  {p.note}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 md:gap-4">
            <Link
              to="/almanac"
              className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition hover-lift"
              data-testid="hero-enter-almanac"
            >
              Read today's FREE guidance <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Sky pulse + personalise DOB — sit between the CTA and poster gallery */}
      <LivePulseTile />
      <PersonaliseCTA />

      <div className="mx-auto max-w-[1200px] px-6 md:px-10 pb-20 md:pb-32">
        <div className="max-w-3xl mx-auto text-center">
          {/* Posters gallery below the pulse / personalise sections */}
          <div className="max-w-4xl mx-auto">
            <IntroReelReplacement />
          </div>

          {/* Meditation recommender + social proof */}
          <div className="mt-12 md:mt-16 flex flex-col items-center gap-8">
            <MeditationRecommender />
            <SocialProof />
          </div>
        </div>
      </div>
      <ScrollHint />
    </section>
  );
}

function ScrollHint() {
  return (
    <div className="flex justify-center pb-10">
      <div className="flex flex-col items-center gap-2 text-[color:var(--text-muted)]">
        <span className="label-overline text-[10px]">breathe in</span>
        <ChevronDown size={16} className="animate-bounce" style={{ animationDuration: "3s" }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EMPATHY — "You feel it, don't you?"
// ─────────────────────────────────────────────────────────────
function Empathy() {
  const signs = [
    { icon: Heart, title: "Migraines before the news of a storm" },
    { icon: Moon, title: "Waking at 3 a.m. for no reason" },
    { icon: Waves, title: "Waves of anxiety you can't trace" },
    { icon: Sun, title: "Days where everything feels too loud" },
    { icon: Compass, title: "Restlessness that softens under moonlight" },
    { icon: Feather, title: "Crying at songs you've heard a hundred times" },
  ];
  return (
    <section className="relative border-t border-[color:var(--gold)]/10" style={{ background: "linear-gradient(180deg, transparent, rgba(212,162,76,0.03))" }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36" data-testid="empathy-section">
        <div className="max-w-3xl">
          <div className="label-overline">You are not imagining it</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight">
            The world <span className="font-serif-italic text-[color:var(--gold)]">shifts</span>, and your body notices.
          </h2>
          <p className="mt-8 text-[color:var(--parchment-soft)] text-lg leading-relaxed max-w-2xl font-sans-body">
            When the sun sends a solar storm toward Earth, our planet's electromagnetic pulse changes. Sensitive people — highly attuned nervous systems, empaths, those who have always &ldquo;felt too much&rdquo; — often feel the shift first, before any headline.
          </p>
        </div>
        <ul className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {signs.map(({ icon: Icon, title }, i) => (
            <li
              key={title}
              className="parchment-card rounded-2xl p-6 md:p-7 hover-lift"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`sensitivity-sign-${i}`}
            >
              <div className="w-10 h-10 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center mb-4">
                <Icon size={16} className="text-[color:var(--gold)]" />
              </div>
              <p className="font-serif-display text-[color:var(--parchment)] text-lg leading-snug">{title}</p>
            </li>
          ))}
        </ul>
        <p className="mt-14 font-serif-italic text-[color:var(--parchment-soft)] text-xl md:text-2xl max-w-2xl leading-relaxed">
          &ldquo;It isn't fragility. It's frequency. Your body is doing what it was built to do — listening.&rdquo;
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// SCIENCE — Prof. Schumann's discovery
// ─────────────────────────────────────────────────────────────
function Science() {
  return (
    <section className="relative border-t border-[color:var(--gold)]/10">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
        <div className="md:col-span-5 stagger-in">
          <SineWaveArt />
          <div className="mt-6 label-overline">1952 · Munich</div>
          <p className="mt-3 font-serif-italic text-[color:var(--parchment-soft)] text-sm leading-relaxed">
            Prof. Winfried Otto Schumann, a German physicist, predicted that the cavity between Earth's surface and the ionosphere would ring at very low frequencies — like a giant, planet-sized bell. Two years later, his student measured it. <strong className="text-[color:var(--gold)] not-italic font-medium">7.83 Hz</strong>. Earth's heartbeat.
          </p>
        </div>
        <div className="md:col-span-7 stagger-in" style={{ animationDelay: "80ms" }}>
          <div className="label-overline">The science, gently</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.08] tracking-tight">
            Earth has a <span className="font-serif-italic text-[color:var(--gold)]">frequency</span>.<br />And so do you.
          </h2>
          <div className="mt-8 space-y-5 text-[color:var(--parchment-soft)] text-lg leading-relaxed font-sans-body max-w-xl">
            <p>The Schumann resonance is a real, measurable pulse — the same pulse the human brain slips into during deep meditation. When solar activity spikes, that pulse shifts, and the whole electromagnetic mood of the planet changes with it.</p>
            <p>You don't need to believe anything to feel it. You just need someone to translate it for you each morning, so you know why today feels the way it does.</p>
          </div>
          <Link to="/almanac" className="mt-10 inline-flex items-center gap-2 font-serif-italic text-[color:var(--gold)] hover:underline" data-testid="science-almanac-link">
            See today's Schumann reading <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SineWaveArt() {
  // A soft animated sine wave — pure SVG, no dependencies.
  return (
    <svg viewBox="0 0 320 120" className="w-full max-w-[380px]" aria-hidden="true">
      <defs>
        <linearGradient id="wave-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#d4a24c" stopOpacity="0" />
          <stop offset="20%" stopColor="#d4a24c" stopOpacity="1" />
          <stop offset="80%" stopColor="#d4a24c" stopOpacity="1" />
          <stop offset="100%" stopColor="#d4a24c" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0,60 C40,10 80,110 120,60 C160,10 200,110 240,60 C280,10 320,110 360,60"
        fill="none"
        stroke="url(#wave-grad)"
        strokeWidth="1.5"
        opacity="0.9"
      />
      <path
        d="M0,60 C40,30 80,90 120,60 C160,30 200,90 240,60 C280,30 320,90 360,60"
        fill="none"
        stroke="url(#wave-grad)"
        strokeWidth="0.75"
        opacity="0.4"
      />
      <text x="0" y="105" fill="#7a7565" fontSize="9" fontFamily="Instrument Sans" letterSpacing="2">7.83 Hz</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// LIVE PULSE TILE — fetches real data
// ─────────────────────────────────────────────────────────────
function LivePulseTile() {
  const [schumann, setSchumann] = useState(null);
  const [cosmic, setCosmic] = useState(null);

  useEffect(() => {
    getSchumannCurrent().then(setSchumann).catch(() => {});
    getCosmicCurrent().then(setCosmic).catch(() => {});
  }, []);

  const activity = schumann?.status || (schumann ? "settled" : "…");
  const baseFreq = schumann?.base_freq_hz ? `${schumann.base_freq_hz.toFixed(2)} Hz` : "—";
  const kp = cosmic?.kp_index != null ? Number(cosmic.kp_index).toFixed(1) : "—";
  const wind = cosmic?.solar_wind_speed ? `${Math.round(cosmic.solar_wind_speed)} km/s` : "—";

  return (
    <section className="relative border-t border-[color:var(--gold)]/10" style={{ background: "linear-gradient(180deg, rgba(212,162,76,0.03), transparent)" }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-32 text-center" data-testid="live-pulse-tile">
        <div className="label-overline flex items-center justify-center gap-3">
          <span className="pulse-dot" /> Live · this moment
        </div>
        <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight">
          The sky right now is <span className="font-serif-italic text-[color:var(--gold)]">{activity}</span>.
        </h2>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-left">
          <PulseStat label="Schumann · base" value={baseFreq} note="Earth's heartbeat" />
          <PulseStat label="Geomagnetic · Kp" value={kp} note="0–9 scale · storms above 5" />
          <PulseStat label="Solar wind" value={wind} note="Charged particles from the sun" />
        </div>
        <Link to="/almanac" className="mt-12 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[color:var(--gold)]/50 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition" data-testid="live-pulse-cta">
          Read today's full briefing <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

function PulseStat({ label, value, note }) {
  return (
    <div className="parchment-card rounded-2xl p-7 text-center hover-lift">
      <div className="label-overline">{label}</div>
      <div className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl tracking-tight">{value}</div>
      <div className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">{note}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MEET THE GUIDANCE — feature grid
// ─────────────────────────────────────────────────────────────
function MeetTheAlmanac() {
  const features = [
    {
      icon: BookOpen,
      title: "A daily briefing, in gentle English",
      body: "Every morning, our narrative engine reads today's Schumann pulse and solar activity and writes you a warm, literary summary — not numbers, just meaning.",
    },
    {
      icon: Feather,
      title: "A journal that listens back",
      body: "Log how you're feeling. Over time you'll see the correlations — days when your energy dips exactly when the sun flares.",
    },
    {
      icon: Sparkles,
      title: "Alerts when the sky gets loud",
      body: "Solar storms and geomagnetic disturbances are surfaced so you can rest, hydrate, and go easy on yourself before it hits.",
    },
  ];
  return (
    <section className="relative border-t border-[color:var(--gold)]/10">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36" data-testid="meet-almanac">
        <div className="max-w-2xl">
          <div className="label-overline">Meet your guidance</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.08] tracking-tight">
            A quiet dashboard for a noisy sky.
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="parchment-card rounded-2xl p-8 hover-lift" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="w-11 h-11 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center mb-5">
                <Icon size={16} className="text-[color:var(--gold)]" />
              </div>
              <h3 className="font-serif-display text-[color:var(--parchment)] text-xl leading-snug">{title}</h3>
              <p className="mt-3 text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// BIRTH CHART TEASER
// ─────────────────────────────────────────────────────────────
function BirthChartTeaser() {
  return (
    <section className="relative border-t border-[color:var(--gold)]/10" style={{ background: "linear-gradient(180deg, transparent, rgba(212,162,76,0.05))" }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
        <div className="md:col-span-6 stagger-in">
          <div className="label-overline">A personal reading</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.02] tracking-tight">
            Your <span className="font-serif-italic text-[color:var(--gold)]">cosmic</span> birth chart.
          </h2>
          <p className="mt-8 text-[color:var(--parchment-soft)] text-lg leading-relaxed font-sans-body max-w-lg">
            A warm, literary reading of your Sun, Moon and Rising — plus a printable natal wheel with all ten planets, the famous people born on your day, the #1 song of your birth week, your lucky numbers, and the callings that suit your chart.
          </p>
          <ul className="mt-8 space-y-3 text-[color:var(--parchment-soft)] font-sans-body">
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-[color:var(--gold)] shrink-0" /> High-resolution natal wheel · screenshot &amp; keep</li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-[color:var(--gold)] shrink-0" /> Written by our proprietary narrative engine</li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-[color:var(--gold)] shrink-0" /> Delivered instantly on screen · email optional</li>
            <li className="flex gap-3"><Sparkles size={14} className="mt-1.5 text-[color:var(--rose-gold)] shrink-0" /> <span><strong className="text-[color:var(--parchment)] font-medium">Share as many charts as your heart wants</strong> — one membership, every friend</span></li>
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/subscribe"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition hover-lift"
              data-testid="birth-chart-cta"
            >
              Start membership · £8.88 / month <ArrowRight size={16} />
            </Link>
            <Link
              to="/birth-chart"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[color:var(--rose-gold)]/60 text-[color:var(--parchment)] hover:bg-[color:var(--rose-gold)]/10 transition"
              data-testid="compat-cta-teaser"
            >
              <Heart size={14} /> Try your first reading
            </Link>
          </div>
          <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs">Cancel any time from Stripe's billing portal. No hidden charges.</p>
        </div>
        <div className="md:col-span-6 flex justify-center md:justify-end stagger-in" style={{ animationDelay: "80ms" }}>
          <SampleWheel />
        </div>
      </div>
    </section>
  );
}

function SampleWheel() {
  // A very simplified decorative natal-wheel preview. The real thing is drawn
  // by the backend once the user pays.
  return (
    <svg viewBox="0 0 320 320" className="w-full max-w-[380px]" aria-label="Sample natal wheel">
      <defs>
        <radialGradient id="disc-lp" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#131b36" />
          <stop offset="100%" stopColor="#050917" />
        </radialGradient>
      </defs>
      <circle cx="160" cy="160" r="152" fill="url(#disc-lp)" stroke="#d4a24c" strokeWidth="1.5" />
      <circle cx="160" cy="160" r="120" fill="none" stroke="#b98a3c" strokeWidth="0.5" opacity="0.6" />
      <circle cx="160" cy="160" r="86" fill="none" stroke="#6b7391" strokeWidth="0.4" opacity="0.35" />
      <circle cx="160" cy="160" r="42" fill="none" stroke="#d4a24c" strokeWidth="0.6" opacity="0.55" />
      {Array.from({ length: 12 }).map((_, i) => {
        const a = ((i * 30) * Math.PI) / 180;
        const x1 = 160 + 120 * Math.cos(a);
        const y1 = 160 + 120 * Math.sin(a);
        const x2 = 160 + 152 * Math.cos(a);
        const y2 = 160 + 152 * Math.sin(a);
        const mx = 160 + 136 * Math.cos(a + (15 * Math.PI) / 180);
        const my = 160 + 136 * Math.sin(a + (15 * Math.PI) / 180);
        const glyphs = ["♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓"];
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#b98a3c" strokeWidth="0.4" opacity="0.5" />
            <text x={mx} y={my} fill="#d4a24c" fontSize="12" textAnchor="middle" dominantBaseline="central">{glyphs[i]}</text>
          </g>
        );
      })}
      {/* Fake planets */}
      {[
        { a: 45, g: "☉", c: "#f5e6c8" },
        { a: 110, g: "☾", c: "#e5a87c" },
        { a: 200, g: "♀", c: "#f5e6c8" },
        { a: 275, g: "♂", c: "#e5a87c" },
        { a: 330, g: "♃", c: "#f5e6c8" },
      ].map((p, i) => {
        const a = (p.a * Math.PI) / 180;
        const x = 160 + 86 * Math.cos(a);
        const y = 160 + 86 * Math.sin(a);
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="10" fill="#050917" stroke="#d4a24c" strokeWidth="0.5" />
            <text x={x} y={y} fill={p.c} fontSize="12" textAnchor="middle" dominantBaseline="central">{p.g}</text>
          </g>
        );
      })}
      <text x="160" y="155" fill="#d4a24c" fontSize="10" textAnchor="middle" letterSpacing="2">NATAL WHEEL</text>
      <text x="160" y="172" fill="#f5e6c8" fontSize="10" textAnchor="middle" fontStyle="italic">a sample</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// HOW IT WORKS
// ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: "01", title: "Land, and breathe", body: "Open Earth Pulse in the morning. See today's Schumann reading, solar wind, and alerts in one glance." },
    { n: "02", title: "Read your guidance", body: "A warm daily briefing translates the science into a two-minute read tailored to how you might feel today." },
    { n: "03", title: "Journal what you notice", body: "Log a mood word. Over weeks, patterns emerge — your body was right all along." },
  ];
  return (
    <section className="relative border-t border-[color:var(--gold)]/10">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36" data-testid="how-it-works">
        <div className="max-w-2xl">
          <div className="label-overline">How it works</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.08] tracking-tight">
            Three quiet steps.
          </h2>
        </div>
        <ol className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {steps.map((s) => (
            <li key={s.n} className="stagger-in">
              <div className="font-serif-display text-[color:var(--gold)] text-6xl md:text-7xl leading-none opacity-70">{s.n}</div>
              <h3 className="mt-6 font-serif-display text-[color:var(--parchment)] text-2xl leading-snug">{s.title}</h3>
              <p className="mt-3 text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// TESTIMONIALS
// ─────────────────────────────────────────────────────────────
function Testimonials() {
  const quotes = [
    {
      body: "I've felt storms in my body since I was ten. Reading your guidance each morning is the first time anyone has translated it back to me. I don't feel mad.",
      name: "Ruth · Somerset",
    },
    {
      body: "The birth chart made me cry. Not because it was flattering — because it noticed things about me my closest friends haven't.",
      name: "Ada · Manchester",
    },
    {
      body: "I sleep worse the night before a Kp-5. Now I know why, and I plan around it. Life-changing, honestly.",
      name: "Marco · Lisbon",
    },
  ];
  return (
    <section className="relative border-t border-[color:var(--gold)]/10" style={{ background: "linear-gradient(180deg, rgba(212,162,76,0.04), transparent)" }}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-36" data-testid="testimonials">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-overline">In their words</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.08] tracking-tight">
            Not fragile. <span className="font-serif-italic text-[color:var(--gold)]">Tuned in.</span>
          </h2>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {quotes.map((q, i) => (
            <blockquote key={i} className="parchment-card rounded-2xl p-8 hover-lift">
              <Sparkles size={14} className="text-[color:var(--gold)]" />
              <p className="mt-5 font-serif-display text-[color:var(--parchment)] text-lg md:text-xl leading-snug">&ldquo;{q.body}&rdquo;</p>
              <cite className="mt-6 block not-italic font-serif-italic text-[color:var(--text-secondary)] text-sm">— {q.name}</cite>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────
function FAQ() {
  const faqs = [
    {
      q: "Is any of this scientifically proven?",
      a: "The Schumann resonance and solar space weather are absolutely real, measurable, and tracked by NASA and NOAA daily — we pull directly from those feeds. The link between geomagnetic activity and how sensitive people feel is a growing area of research; we present it gently as pattern-noticing, never as medical or predictive advice.",
    },
    {
      q: "Do I need to know anything about astrology or astronomy?",
      a: "Not a thing. The daily briefing translates the science into calm, warm prose. Think of it as the horoscope column, if the horoscope column was written by a physicist with a heart.",
    },
    {
      q: "How is the birth chart different from a free horoscope?",
      a: "Free horoscopes use only your Sun sign. Your birth chart uses your Sun, Moon, Rising and all ten major planets — computed with the same Swiss Ephemeris professional astrologers use. Then our narrative engine writes a personal reading, and you get a printable natal wheel you can keep forever.",
    },
    {
      q: "Will you ever send me spam?",
      a: "Never. If you sign up for the weekly cosmic recap, it's one Sunday-night email — no more. Unsubscribe with one click, and we won't sell or share your email with anyone.",
    },
    {
      q: "Is this a subscription?",
      a: "The daily guidance is free. Unlimited Birth Charts and Compatibility readings are on an £8.88/month subscription — cancel any time from Stripe's billing portal. No hidden charges, no long commitment.",
    },
    {
      q: "I'm not really into 'woo woo'. Is this for me?",
      a: "Yes. Earth Pulse is grounded in measurable physics. If woo woo isn't your thing, use your guidance and skip the birth chart. If you're curious, you're welcome to explore both.",
    },
  ];
  return (
    <section className="relative border-t border-[color:var(--gold)]/10">
      <div className="mx-auto max-w-[860px] px-6 md:px-10 py-24 md:py-36" data-testid="faq-section">
        <div className="max-w-2xl">
          <div className="label-overline">Questions, gently answered</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.08] tracking-tight">
            Things sensitive people ask.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-12 space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="parchment-card rounded-2xl px-6 border-none" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-serif-display text-[color:var(--parchment)] text-lg md:text-xl hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[color:var(--parchment-soft)] leading-relaxed font-sans-body pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// NEWSLETTER
// ─────────────────────────────────────────────────────────────
function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email, "landing");
      setDone(true);
      toast.success("You're on the list", { description: "The first cosmic recap arrives on Sunday." });
    } catch (err) {
      toast.error("Something went wrong", { description: err?.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative border-t border-[color:var(--gold)]/10" style={{ background: "linear-gradient(180deg, transparent, rgba(212,162,76,0.05))" }}>
      <div className="mx-auto max-w-[820px] px-6 md:px-10 py-24 md:py-32 text-center" data-testid="newsletter-section">
        <Mail size={22} className="mx-auto text-[color:var(--gold)]" />
        <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.05] tracking-tight">
          A quiet Sunday letter.
        </h2>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed max-w-xl mx-auto">
          One email each Sunday evening — the week's Schumann highlights, any storms on the horizon, and something soft to read with tea.
        </p>
        {done ? (
          <p className="mt-10 font-serif-italic text-[color:var(--gold)] text-lg" data-testid="newsletter-done">
            Thank you. See you Sunday.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-[color:var(--bg-surface)] border border-[color:var(--gold)]/25 rounded-full px-6 py-3.5 text-[color:var(--parchment)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--gold)]/60"
              data-testid="newsletter-email"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="px-7 py-3.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium disabled:opacity-60 transition"
              data-testid="newsletter-submit"
            >
              {loading ? "…" : "Send me Sundays"}
            </button>
          </form>
        )}
        <p className="mt-6 font-serif-italic text-[color:var(--text-muted)] text-xs">One email a week. Unsubscribe any time. Never shared.</p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="relative border-t border-[color:var(--gold)]/15" style={{ background: "rgba(5,9,23,0.6)" }}>
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-14">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
              <Sparkles size={14} className="text-[color:var(--gold)]" />
            </div>
            <div>
              <div className="font-serif-display text-[color:var(--parchment)] text-lg">Earth · Pulse</div>
              <div className="label-overline text-[10px]">earthpulse.store</div>
            </div>
          </div>
          <p className="mt-5 font-serif-italic text-[color:var(--parchment-soft)] max-w-md leading-relaxed">
            Daily cosmic guidance for people who feel it first. Schumann resonance, space weather, and gentle astrology — grounded in real data from NOAA and NASA.
          </p>
        </div>
        <div>
          <div className="label-overline">Explore</div>
          <ul className="mt-5 space-y-3 text-[color:var(--parchment-soft)] font-sans-body">
            <li><Link to="/almanac" className="hover:text-[color:var(--parchment)]" data-testid="footer-almanac">Today's guidance</Link></li>
            <li><Link to="/subscribe" className="hover:text-[color:var(--parchment)]" data-testid="footer-subscribe">Membership · £8.88 / month</Link></li>
            <li><Link to="/birth-chart" className="hover:text-[color:var(--parchment)]" data-testid="footer-birth-chart">Cosmic Birth Chart</Link></li>
            <li><Link to="/compatibility" className="hover:text-[color:var(--parchment)]" data-testid="footer-compat">Compatibility · Synastry</Link></li>
            <li><Link to="/subscription" className="hover:text-[color:var(--parchment)]" data-testid="footer-manage">Manage membership</Link></li>
            <li><Link to="/my-readings" className="hover:text-[color:var(--parchment)]" data-testid="footer-my-readings">Lost your reading link?</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-overline">Legal</div>
          <ul className="mt-5 space-y-3 text-[color:var(--parchment-soft)] font-sans-body">
            <li><Link to="/privacy" className="hover:text-[color:var(--parchment)]">Privacy</Link></li>
            <li><Link to="/terms" className="hover:text-[color:var(--parchment)]">Terms</Link></li>
            <li><Link to="/cookies" className="hover:text-[color:var(--parchment)]">Cookies</Link></li>
            <li><a href="mailto:hello@earthpulse.store" className="hover:text-[color:var(--parchment)]">hello@earthpulse.store</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[color:var(--gold)]/10">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-serif-italic text-[color:var(--text-muted)] text-xs">
            © {new Date().getFullYear()} Earth · Pulse · A quiet corner of the internet
          </p>
          <p className="label-overline text-[10px]">Data · NOAA SWPC · ResonanceOne · Swiss Ephemeris</p>
        </div>
      </div>
    </footer>
  );
}
